import { createAction, props } from '@ngrx/store';
import {
  ApproveRequest,
  AssignmentCustomerResponse,
  AssignmentDateContextResponse,
  AssignmentDateListItemResponse,
  AssignmentDateTimestampListItemResponse,
  AssignmentDocumentUpdateRequest,
  AssignmentPspResponse,
  CancelationRequest,
  CommentResponse,
  CreateAssignmentRequest,
  CreateCommentRequest,
  CustomerAssignmentFilterListsResponse,
  CustomerAssignmentListItemResponse,
  CustomerDepartmentQualificationsListItemResponse,
  CustomerDepartmentResponse,
  CustomerProfileAssignmentItemResponse,
  CustomerProfilePersonalIdRequest,
  CustomerProfileRatingCreateRequest,
  CustomerProfileRatingListResponse,
  CustomerProfileRatingUpdateRequest,
  CustomerResponse,
  DisapproveRequest,
  GeneralApiResponse,
  ListApiResponse,
  MetaPagination,
  NoteRequest,
  PaginatedApiResponseWithFilters,
  PspAssignmentListItemResponse,
  PspProfilePersonalIdRequest,
  RegenerationRequest,
  UpdateRequest,
  UpdateWorkTimesRequest,
} from '../../../api';
import { BaseSignContractOptions } from '../../modules/assignments/_enums/base-sign-contract-options.enum';
import { ExportCustomerContractsRequestInterface } from '../../modules/assignments/_interfaces/export-customer-contracts-request.interface';
import { ExportPspContractsRequestInterface } from '../../modules/assignments/_interfaces/export-psp-contracts-request.interface';
import { ExportPspAssignmentInformationRequestInterface } from '../../modules/assignments/_interfaces/export-psp-assignment-information-request.interface';
import { ExportCustomerAssignmentInformationRequestInterface } from '../../modules/assignments/_interfaces/export-customer-assignment-information-request.interface';
import { BaseAssignmentsListPayloadInterface } from '../../modules/assignments/_interfaces/base-assignments-list-payload.interface';

// Assignment list
export const loadAssignmentList = createAction(
  '[Assignment list] Load assignment list',
  props<{
    payload: BaseAssignmentsListPayloadInterface;
  }>()
);
export const setAssignmentList = createAction(
  '[Assignment list] Set assignment list',
  props<{
    payload: ListApiResponse & { meta: MetaPagination } & {
      data?: CustomerAssignmentListItemResponse[] | PspAssignmentListItemResponse[] | undefined;
    };
  }>()
);
export const errorAssignmentList = createAction('[Assignment list] Error assignment list');

// Customer all assignments
export const loadCustomerAllAssignments = createAction(
  '[Customer Assignments] Load all customer assignments',
  props<{
    payload: BaseAssignmentsListPayloadInterface;
  }>()
);
export const setCustomerAllAssignments = createAction(
  '[Customer Assignments] Set all customer assignments',
  props<{
    payload: PaginatedApiResponseWithFilters & {
      data?: Array<CustomerAssignmentListItemResponse>;
      filters?: CustomerAssignmentFilterListsResponse;
    };
  }>()
);
export const errorCustomerAllAssignment = createAction('[Customer Assignments] Error assignment list');

export const successCustomerAllAssignment = createAction('[Customer Assignments] Success assignment list');

// Assignment details
export const loadAssignment = createAction(
  '[Assignment] Load assignment',
  props<{ payload: { assignment: number; showDigitalSignStatus?: boolean } }>()
);
export const setAssignment = createAction(
  '[Assignment] Set assignment',
  props<{ payload: AssignmentPspResponse | AssignmentCustomerResponse }>()
);
export const successLoadAssignment = createAction('[Assignment] Success load assignment');
export const errorLoadAssignment = createAction('[Assignment] Error load assignment');
export const resetAssignment = createAction('[Assignment] Reset assignment');

// Confirm assignment
export const confirmAssignment = createAction(
  '[Assignment] Confirm assignment',
  props<{ payload: { assignment: number; assignmentDate: number } }>()
);
export const confirmAssignmentSuccess = createAction('[Assignment] Confirm assignment success');
export const confirmAssignmentError = createAction('[Assignment] Confirm assignment error');

// Assignment dates
export const loadAssignmentDates = createAction(
  '[Assignment details] Load assignment dates',
  props<{ payload: any }>()
);

export const setAssignmentDates = createAction(
  '[Assignment details] Set assignment dates',
  props<{ payload: AssignmentDateListItemResponse[] }>()
);

export const successAssignmentDates = createAction('[Assignment details] Success assignment dates');

export const errorAssignmentDates = createAction('[Assignment details] Error assignment dates');

export const updateAssignmentDate = createAction(
  '[Assignment] Update assignment date',
  props<{ payload: AssignmentDateListItemResponse }>()
);

// Create / update assignment times'
export const createUpdateAssignmentTimes = createAction(
  '[Assignment details] Create or update assignment times',
  props<{
    payload: {
      assignment: number;
      assignmentDate: number;
      body: UpdateWorkTimesRequest;
    };
  }>()
);
export const createUpdateAssignmentTimesSuccess = createAction(
  '[Assignment details] Create or update assignment times success'
);
export const createUpdateAssignmentTimesError = createAction(
  '[Assignment details] Create or update assignment times error'
);

// Sign contract
export const signContract = createAction(
  '[Assignment details] Sign contract',
  props<{
    payload: {
      assignment: number;
      contract: number;
      signingType: BaseSignContractOptions;
      reloadAssignmentInfo?: boolean;
    };
  }>()
);
export const signContractSuccess = createAction('[Assignment details] Sign contract customer success');
export const signContractError = createAction('[Assignment details] Sign contract customer error');
export const clearContractStatus = createAction('[Assignment details] Clear Sign contract customer status');

// Assignment cancellation
export const assignmentCustomerCancellation = createAction(
  '[Customer Assignments] Assignment cancellation',
  props<{ payload: { assignment: number; body: CancelationRequest } }>()
);

export const assignmentCancellation = createAction(
  '[Assignment details] Assignment cancellation',
  props<{ payload: { assignment: number; body: CancelationRequest } }>()
);
export const assignmentCancellationSuccess = createAction('[Assignment details] Assignment cancellation success');
export const assignmentCancellationError = createAction('[Assignment details] Assignment cancellation error');

// Candidate ended assignments
export const getCandidateEndedAssignments = createAction(
  '[Assignment] Get candidate ended assignments',
  props<{ payload: number }>()
);
export const loadCandidateEndedAssignments = createAction(
  '[Assignment] Load candidate ended assignments',
  props<{ payload: Array<CustomerProfileAssignmentItemResponse> }>()
);
export const loadCandidateEndedAssignmentsSuccess = createAction(
  '[Assignment] Load candidate ended assignments success'
);
export const loadCandidateEndedAssignmentsError = createAction('[Assignment] Load candidate ended assignments error');

// Customer reviews
export const getCustomerReviews = createAction('[Assignment] Get customer reviews', props<{ payload: number }>());
export const loadCustomerReviews = createAction(
  '[Assignment] Load customer reviews',
  props<{ payload: CustomerProfileRatingListResponse }>()
);
export const loadCustomerReviewsSuccessfully = createAction('[Assignment] Load customer review successfully');
export const loadCustomerReviewsError = createAction('[Assignment] Load customer review error');
export const clearCustomerReviews = createAction('[Assignment] Clear customer review');

// Rate candidate
export const createCandidateRating = createAction(
  '[Assignment] Create candidate rating',
  props<{
    payload: {
      body: CustomerProfileRatingCreateRequest;
      assignment?: CustomerAssignmentListItemResponse | AssignmentCustomerResponse;
      profileId?: number;
    };
  }>()
);
export const createCandidateRatingSuccess = createAction('[Assignments] Create candidate rating success');
export const createCandidateRatingError = createAction('[Assignment] Create candidate rating error');

export const updateCandidateRating = createAction(
  '[Assignment] Update candidate rating',
  props<{
    payload: {
      rating: number;
      body: CustomerProfileRatingUpdateRequest;
      assignment?: CustomerAssignmentListItemResponse | AssignmentCustomerResponse;
      profileId?: number;
    };
  }>()
);
export const updateCandidateRatingSuccess = createAction('[Assignment] Update candidate rating success');
export const updateCandidateRatingError = createAction('[Assignment] Update candidate rating error');

// Assignment customer
export const loadAssignmentCustomer = createAction(
  '[Assignment customer] Load customer info',
  props<{ payload: { customer: number; psp?: number } }>()
);
export const setAssignmentCustomer = createAction(
  '[Assignment customer] Set customer',
  props<{ payload: GeneralApiResponse & { data?: CustomerResponse } }>()
);
export const errorAssignmentCustomer = createAction('[Assignment customer] Error customer');

// View contract
export const viewContract = createAction(
  '[Assignment] Download contract',
  props<{ payload: { assignment: number; contract: number } }>()
);
export const successDownloadContract = createAction('[Assignment] Download contract success');
export const errorDownloadContract = createAction('[Assignment] Download contract error');

// Download customer contracts
export const downloadCustomerContracts = createAction(
  '[Assignment] Download Customer Contracts',
  props<{
    payload: { contractsInfo: Partial<ExportCustomerContractsRequestInterface>; noContractProfiles: string[] };
  }>()
);
export const successDownloadCustomerContracts = createAction('[Assignment] Success Download Customer Contracts');
export const errorDownloadCustomerContracts = createAction('[Assignment] Error Download Customer Contracts');

// Download customer assignment information
export const downloadCustomerAssignmentInfo = createAction(
  '[Assignment] Download Customer Assignment Information',
  props<{ payload: Partial<ExportCustomerAssignmentInformationRequestInterface> }>()
);
export const successDownloadCustomerAssignmentInfo = createAction(
  '[Assignment] Success Download Customer Assignment Information'
);
export const errorDownloadCustomerAssignmentInfo = createAction(
  '[Assignment] Error Download Customer Assignment Information'
);

// Update customer profile personal id
export const updateCustomerProfilePersonalId = createAction(
  '[Assignment] Update customer profile personal id',
  props<{ payload: { profile: number; body: CustomerProfilePersonalIdRequest; assignment: number } }>()
);
export const successUpdateCustomerProfilePersonalId = createAction(
  '[Assignment] Success Update customer profile personal id'
);
export const errorUpdateCustomerProfilePersonalId = createAction(
  '[Assignment] Error Update customer profile personal id'
);

// Download PSP contracts
export const downloadPspContracts = createAction(
  '[Assignment] Download PSP Contracts',
  props<{ payload: { contractsInfo: Partial<ExportPspContractsRequestInterface>; noContractProfiles: string[] } }>()
);
export const successDownloadPspContracts = createAction('[Assignment] Success Download PSP Contracts');
export const errorDownloadPspContracts = createAction('[Assignment] Error Download PSP Contracts');
// Download PSP assignment information
export const downloadPspAssignmentInfo = createAction(
  '[Assignment] Download PSP Assignment Information',
  props<{ payload: Partial<ExportPspAssignmentInformationRequestInterface> }>()
);
export const successDownloadPspAssignment = createAction('[Assignment] Success Download PSP Assignment Information');
export const errorDownloadPspAssignment = createAction('[Assignment] Error Download PSP Assignment Information');

// Customer candidate document
export const downloadCustomerCandidateDocument = createAction(
  '[Assignment] Download customer candidate document',
  props<{ payload: { assignment: number; document: string; documentType: string; openInModal?: boolean } }>()
);
export const setDocumentView = createAction(
  '[Assignment] Set document view',
  props<{ payload: { document: Blob; fileName?: string } }>()
);
export const clearDocumentView = createAction('[Assignment] Clear document view');
export const successDownloadCustomerCandidateDocument = createAction(
  '[Assignment] Download customer candidate document success'
);
export const errorDownloadCustomerCandidateDocument = createAction(
  '[Assignment] Download customer candidate document error'
);

export const regenerateCustomerAssignmentContract = createAction(
  '[Assignment] Regenerate Customer assignment contract',
  props<{ payload: { assignment: number; body?: RegenerationRequest } }>()
);

export const successRegenerateCustomerAssignmentContract = createAction(
  '[Assignment] Regenerate Customer assignment contract success'
);
export const errorRegenerateCustomerAssignmentContract = createAction(
  '[Assignment] Regenerate Customer assignment contract error'
);

export const regeneratePspAssignmentContract = createAction(
  '[Assignment] Regenerate PSP assignment contract',
  props<{ payload: { assignment: number; body?: RegenerationRequest } }>()
);

export const successRegeneratePspAssignmentContract = createAction(
  '[Assignment] Regenerate PSP assignment contract success'
);
export const errorRegeneratePspAssignmentContract = createAction(
  '[Assignment] Regenerate PSP assignment contract error'
);

export const regenerateAssignmentProfileIDCard = createAction(
  '[Assignment] Regenerate assignment profile ID card',
  props<{ payload: { assignment: number; profile: number } }>()
);

export const successRegenerateAssignmentProfileIDCard = createAction(
  '[Assignment] Regenerate assignment profile ID card success'
);
export const errorRegenerateAssignmentProfileIDCard = createAction(
  '[Assignment] Regenerate assignment profile ID card error'
);

export const downloadAssignmentProfileIDCard = createAction(
  '[Administration] Download assignment profile ID card',
  props<{ payload: { assignment: number; profile: number; openInModal?: boolean; downloadOnly?: boolean } }>()
);
export const successDownloadAssignmentProfileIDCard = createAction(
  '[Administration] Success Download assignment profile ID card'
);
export const errorDownloadAssignmentProfileIDCard = createAction(
  '[Administration] Error Download assignment profile ID card'
);

export const setAssignmentProfileIDCardView = createAction(
  '[Administration] Set assignment profile ID card view',
  props<{ payload: { document: Blob; fileName: string } }>()
);
export const clearAssignmentProfileIDCardView = createAction('[Administration] Clear assignment profile ID card view');

// Approve inquiry offer
export const approveInquiryOffer = createAction(
  '[Assignment] Approve inquiry offer',
  props<{ payload: { offer: number; body: ApproveRequest; assignmentId?: number; inquiryId?: number } }>()
);
export const successApproveInquiryOffer = createAction('[Assignment] Success Approve inquiry offer');
export const errorApproveInquiryOffer = createAction('[Assignment] Error Approve inquiry offer');

// Disapprove inquiry offer
export const disapproveInquiryOffer = createAction(
  '[Assignment] Disapprove inquiry offer',
  props<{ payload: { offer: number; body: DisapproveRequest; assignmentId?: number; inquiryId?: number } }>()
);
export const successDisapproveInquiryOffer = createAction('[Assignment] Success Disapprove inquiry offer');
export const errorDisapproveInquiryOffer = createAction('[Assignment] Error Disapprove inquiry offer');

// Note inquiry offer
export const noteInquiryOffer = createAction(
  '[Assignment] Note inquiry offer',
  props<{ payload: { offer: number; body: NoteRequest; assignmentId?: number; inquiryId?: number } }>()
);
export const successNoteInquiryOffer = createAction('[Assignment] Success Note inquiry offer');
export const errorNoteInquiryOffer = createAction('[Assignment] Error Note inquiry offer');

// Create customer offer comment
export const createWorkCouncilComment = createAction(
  '[Assignment] Create Work Council comment',
  props<{ payload: { offer: number; body: CreateCommentRequest } }>()
);
export const successCreateWorkCouncilComment = createAction('[Assignment] Success Create Work Council comment');
export const errorCreateWorkCouncilComment = createAction('[Assignment] Error Create Work Council comment');

// Create customer offer comment
export const loadWorkCouncilCommentList = createAction(
  '[Assignment] Load Work Council comment list',
  props<{ payload: { offer: number } }>()
);
export const setWorkCouncilCommentList = createAction(
  '[Assignment] Set Work Council comment list',
  props<{ payload: CommentResponse[] }>()
);
export const successWorkCouncilCommentList = createAction('[Assignment] Success Work council comment list');
export const errorWorkCouncilCommentList = createAction('[Assignment] Error Work Council comment list');
export const clearWorkCouncilCommentList = createAction('[Assignment] Clear Work Council comment list');

// Assignments with upcoming EP date
export const loadUpcomingEPAssignments = createAction('[Assignments] Load assignment list with upcoming EP date');
export const setUpcomingEPAssignments = createAction(
  '[Assignments] Set  assignment list with upcoming EP date',
  props<{
    payload: {
      assignments: CustomerAssignmentListItemResponse[] | PspAssignmentListItemResponse[];
      totalCount: number;
    };
  }>()
);
export const errorUpcomingEPAssignments = createAction('[Assignments] Error  assignment list with upcoming EP date');
export const successUpcomingEPAssignments = createAction(
  '[Assignments] Success  assignment list with upcoming EP date'
);

// Assignments with upcoming MLP date
export const loadUpcomingMLPAssignments = createAction('[Assignments] Load assignment list with upcoming MLP date');
export const setUpcomingMLPAssignments = createAction(
  '[Assignments] Set  assignment list with upcoming MLP date',
  props<{
    payload: {
      assignments: CustomerAssignmentListItemResponse[] | PspAssignmentListItemResponse[];
      totalCount: number;
    };
  }>()
);
export const errorUpcomingMLPAssignments = createAction('[Assignments] Error  assignment list with upcoming MLP date');
export const successUpcomingMLPAssignments = createAction(
  '[Assignments] Success  assignment list with upcoming MLP date'
);

// Location Department Qualification List
export const loadLocationDepartmentQualificationList = createAction(
  '[Assignments] Load location department qualification list',
  props<{
    payload: {
      customer: number;
      location: number;
      department: number;
      category?: number;
      tag?: 'business' | 'medical';
      search?: string;
    };
  }>()
);
export const setLocationDepartmentQualificationList = createAction(
  '[Assignments] Set location department qualification list',
  props<{
    payload: CustomerDepartmentQualificationsListItemResponse[];
  }>()
);
export const successLocationDepartmentQualificationList = createAction(
  '[Assignments] Success location department qualification list'
);
export const errorLocationDepartmentQualificationList = createAction(
  '[Assignments] Error location department qualification list'
);
export const resetLocationDepartmentQualificationList = createAction(
  '[Assignments] Reset location department qualification list'
);

// Customer Location Department
export const loadPspCustomerLocationDepartment = createAction(
  '[Assignments] Load customer location department',
  props<{
    payload: {
      customer: number;
      location: number;
      department: number;
    };
  }>()
);
export const setPspCustomerLocationDepartment = createAction(
  '[Assignments] Set customer location department',
  props<{
    payload: CustomerDepartmentResponse;
  }>()
);
export const successPspCustomerLocationDepartment = createAction('[Assignments] Success customer location department');
export const errorPspCustomerLocationDepartment = createAction('[Assignments] Error customer location department');
export const resetPspCustomerLocationDepartment = createAction('[Assignments] Reset customer location department');

// PSP Assignment Date List
export const loadPspAssignmentDateList = createAction(
  '[Assignments] Load PSP assignment date list',
  props<{
    payload: {
      profile: number;
    };
  }>()
);
export const setPspAssignmentDateList = createAction(
  '[Assignments] Set PSP assignment date list',
  props<{
    payload: Array<string>;
  }>()
);
export const successPspAssignmentDateList = createAction('[Assignments] Success PSP assignment date list');
export const errorPspAssignmentDateList = createAction('[Assignments] Error PSP assignment date list');
export const resetPspAssignmentDateList = createAction('[Assignments] Reset PSP assignment date list');

// Create a PSP assignment
export const createPspAssignment = createAction(
  '[Assignments] Create PSP assignment',
  props<{
    payload: {
      body: CreateAssignmentRequest;
    };
  }>()
);
export const successCreatePspAssignment = createAction('[Assignments] Success Create PSP assignment');
export const errorCreatePspAssignment = createAction('[Assignments] Error Create PSP assignment');

// Upload assignment contract
export const uploadAssignmentContract = createAction(
  '[Assignments] Upload Assignment Contract',
  props<{
    payload: {
      file: File;
      callback: (contractId: number) => void;
    };
  }>()
);
export const uploadAssignmentContractSuccess = createAction('[Assignments] Success Upload Assignment Contract');
export const uploadAssignmentContractError = createAction('[Assignments] Error Upload Assignment Contract');

// Update PSP profile personal id
export const updatePSPProfilePersonalId = createAction(
  '[Assignment] Update PSP profile personal id',
  props<{ payload: { assignment: number; profile: number; body: PspProfilePersonalIdRequest } }>()
);
export const successUpdatePSPProfilePersonalId = createAction('[Assignment] Success Update PSP profile personal id');
export const errorUpdatePSPProfilePersonalId = createAction('[Assignment] Error Update PSP profile personal id');

// Assignment date timestamp list
export const loadAssignmentDateTimestampList = createAction(
  '[Assignment Date Timestamps] Load assignment date timestamp list',
  props<{ payload: { customer: number; assignmentDate: number } }>()
);
export const setAssignmentDateTimestampList = createAction(
  '[Assignment Date Timestamps] Set assignment date timestamp list',
  props<{
    payload: {
      assignmentDate?: AssignmentDateContextResponse;
      timestamps?: AssignmentDateTimestampListItemResponse[];
    };
  }>()
);
export const successAssignmentDateTimestampList = createAction(
  '[Assignment Date Timestamps] Success assignment date timestamp list'
);
export const errorAssignmentDateTimestampList = createAction(
  '[Assignment Date Timestamps] Error assignment date timestamp list'
);
export const resetAssignmentDateTimestampList = createAction(
  '[Assignment Date Timestamps] Reset assignment date timestamp list'
);

// Update assignment date timestamps
export const updateAssignmentDateTimestamps = createAction(
  '[Assignment Date Timestamps] Update assignment date timestamps',
  props<{ payload: { customer: number; assignmentDate: number; body: UpdateRequest } }>()
);
export const successUpdateAssignmentDateTimestamps = createAction(
  '[Assignment Date Timestamps] Success update assignment date timestamps'
);
export const errorUpdateAssignmentDateTimestamps = createAction(
  '[Assignment Date Timestamps] Error update assignment date timestamps'
);

// PSP assignment documents
/**
 * One action for the whole two-step upload: the effect posts the file and then describes it. The
 * backend only lets the uploader attach an unowned document, so both calls must stay in one flow.
 */
export const uploadPspAssignmentDocument = createAction(
  '[Assignment] Upload PSP assignment document',
  props<{ payload: { assignment: number; file: File; body: AssignmentDocumentUpdateRequest } }>()
);
export const successUploadPspAssignmentDocument = createAction('[Assignment] Success upload PSP assignment document');
export const errorUploadPspAssignmentDocument = createAction('[Assignment] Error upload PSP assignment document');
